import { useState } from "react";
import PageShell from "../components/layout/PageShell";
import {
    getProjectDiagnostics,
    getModuleSnapshot,
    getRedeployRuns,
    insertRedeployRun,
    getSeedSnapshot,
    validateJson,
    fixWebsiteIdentity,
    pushTestItems,
    getSiteFileSnapshot,
    getGeneratedFiles,
    getSmartRedeployPreview,
    queueSmartRedeploy,
    getDeployVersions,
    rollbackVersion
} from "../services/devDiagnosticsService";

import {
    getAiProviders,
    testTextProvider,
    testImageProvider,
    getDbAiProviderRoutes,
    testDbAiProviderRoute,
    testDbAiRouting
} from "../services/devAiDiagnosticsService";

export default function DevDiagnosticsPage() {
    const [projectId, setProjectId] = useState("");
    const [industry, setIndustry] = useState("");
    const [jsonText, setJsonText] = useState("{\n  \"test\": true\n}");
    const [loading, setLoading] = useState("");
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [smartModuleKey, setSmartModuleKey] = useState("products");
    const [selectedVersion, setSelectedVersion] = useState("");

    const [aiProviders, setAiProviders] = useState(null);
    const [providerType, setProviderType] = useState("text");
    const [selectedProvider, setSelectedProvider] = useState("");
    const [aiPrompt, setAiPrompt] = useState("Say hello from Shriansh Nexus diagnostics.");
    const [styleHint, setStyleHint] = useState("premium modern");
    const [layoutType, setLayoutType] = useState("hero");

    const [dbRoutes, setDbRoutes] = useState(null);
    const [selectedDbRouteId, setSelectedDbRouteId] = useState("");
    const [dbFeatureKey, setDbFeatureKey] = useState("*");
    const [dbRoutingMode, setDbRoutingMode] = useState("Balanced");

    const run = async (key, fn) => {
        try {
            setLoading(key);
            setError("");
            const data = await fn();
            setResult(data);
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data
                    ? JSON.stringify(err.response.data, null, 2)
                    : err.message || "Request failed"
            );
        } finally {
            setLoading("");
        }
    };

    const requireProjectId = () => {
        const id = projectId?.trim();
        if (!id) {
            throw new Error("Project Id is required.");
        }
        return id;
    };

    return (
        <PageShell title="Dev Diagnostics Playground">
            <div style={styles.page}>
                <section style={styles.hero}>
                    <div>
                        <h2 style={styles.title}>Shriansh Nexus Diagnostics</h2>
                        <p style={styles.subtitle}>
                            Validate project health, CRM modules, redeploy queue, seeds, and JSON payloads.
                        </p>
                    </div>
                </section>

                <section style={styles.card}>
                    <h3 style={styles.cardTitle}>Project Diagnostics</h3>

                    <div style={styles.row}>
                        <label style={styles.field}>
                            <span>Project Id</span>
                            <input
                                value={projectId}
                                onChange={(e) => setProjectId(e.target.value)}
                                placeholder="Example: 123"
                                style={styles.input}
                            />
                        </label>

                        <button
                            className="btn btn-primary"
                            disabled={loading === "project"}
                            onClick={() =>
                                run("project", () => getProjectDiagnostics(requireProjectId()))
                            }
                        >
                            {loading === "project" ? "Checking..." : "Check Project"}
                        </button>

                        <button
                            className="btn btn-secondary"
                            disabled={loading === "modules"}
                            onClick={() =>
                                run("modules", () => getModuleSnapshot(requireProjectId()))
                            }
                        >
                            CRM Modules
                        </button>

                        <button
                            className="btn btn-secondary"
                            disabled={loading === "redeploy-runs"}
                            onClick={() =>
                                run("redeploy-runs", () => getRedeployRuns(requireProjectId()))
                            }
                        >
                            Redeploy Runs
                        </button>
                    </div>
                </section>

                <section style={styles.card}>
                    <h3 style={styles.cardTitle}>Redeploy Test</h3>

                    <div style={styles.row}>
                        <button
                            className="btn btn-primary"
                            disabled={loading === "insert-redeploy"}
                            onClick={() =>
                                run("insert-redeploy", () =>
                                    insertRedeployRun({
                                        projectId: requireProjectId(),
                                        moduleKey: "dev-diagnostics",
                                        status: "pending",
                                        reason: "Manual diagnostics test from Admin CRM"
                                    })
                                )
                            }
                        >
                            Insert Pending Redeploy Run
                        </button>
                    </div>

                    <p style={styles.hint}>
                        After clicking this, wait a few seconds and run “Redeploy Runs”.
                        If status stays Pending, worker is not picking the job.
                    </p>
                </section>

                <section style={styles.card}>
                    <h3 style={styles.cardTitle}>AI Provider Playground</h3>

                    <div style={styles.row}>
                        <button
                            className="btn btn-secondary"
                            onClick={() =>
                                run("ai-providers", async () => {
                                    const data = await getAiProviders();
                                    setAiProviders(data);

                                    const first =
                                        providerType === "text"
                                            ? data.textProviders?.[0]?.name
                                            : data.imageProviders?.[0]?.name;

                                    if (first) setSelectedProvider(first);
                                    return data;
                                })
                            }
                        >
                            Load AI Providers
                        </button>

                        <select
                            value={providerType}
                            onChange={(e) => {
                                const next = e.target.value;
                                setProviderType(next);

                                const first =
                                    next === "text"
                                        ? aiProviders?.textProviders?.[0]?.name
                                        : aiProviders?.imageProviders?.[0]?.name;

                                if (first) setSelectedProvider(first);
                            }}
                        >
                            <option value="text">Text Provider</option>
                            <option value="image">Image Provider</option>
                        </select>

                        <select
                            value={selectedProvider}
                            onChange={(e) => setSelectedProvider(e.target.value)}
                        >
                            {(providerType === "text"
                                ? aiProviders?.textProviders || []
                                : aiProviders?.imageProviders || []
                            ).map((p) => (
                                <option key={p.name} value={p.name}>
                                    {p.name} {p.isEnabled === false ? "(disabled)" : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    <textarea
                        rows={5}
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder="Enter test prompt"
                        style={{ width: "100%", marginTop: 12 }}
                    />

                    {providerType === "image" && (
                        <div style={styles.row}>
                            <input
                                value={styleHint}
                                onChange={(e) => setStyleHint(e.target.value)}
                                placeholder="Style hint"
                            />
                            <input
                                value={layoutType}
                                onChange={(e) => setLayoutType(e.target.value)}
                                placeholder="Layout type: hero/gallery/logo"
                            />
                        </div>
                    )}

                    <div style={styles.row}>
                        <button
                            className="btn btn-primary"
                            disabled={!selectedProvider}
                            onClick={() =>
                                run("ai-provider-test", () =>
                                    providerType === "text"
                                        ? testTextProvider(selectedProvider, aiPrompt)
                                        : testImageProvider(selectedProvider, aiPrompt, styleHint, layoutType)
                                )
                            }
                        >
                            Run Provider Test
                        </button>

                        <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
                            <h4 style={{ margin: "0 0 8px" }}>DB Route Diagnostics</h4>

                            <div style={styles.row}>
                                <button
                                    className="btn btn-secondary"
                                    disabled={loading === "db-ai-routes"}
                                    onClick={() =>
                                        run("db-ai-routes", async () => {
                                            const data = await getDbAiProviderRoutes();
                                            setDbRoutes(data);

                                            const first = data?.items?.[0]?.id;
                                            if (first) setSelectedDbRouteId(first);

                                            return data;
                                        })
                                    }
                                >
                                    Load DB Routes
                                </button>

                                <select
                                    value={selectedDbRouteId}
                                    onChange={(e) => setSelectedDbRouteId(e.target.value)}
                                    style={styles.input}
                                >
                                    {(dbRoutes?.items || []).map((route) => (
                                        <option key={route.id} value={route.id}>
                                            {route.providerName} / {route.routingMode} / {route.featureKey} / {route.model}
                                            {route.isEnabled === false ? " (disabled)" : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={styles.row}>
                                <input
                                    value={dbFeatureKey}
                                    onChange={(e) => setDbFeatureKey(e.target.value)}
                                    placeholder="Feature key: * / website-generation / crm-module-generate"
                                    style={styles.input}
                                />

                                <select
                                    value={dbRoutingMode}
                                    onChange={(e) => setDbRoutingMode(e.target.value)}
                                    style={styles.input}
                                >
                                    <option value="Balanced">Balanced</option>
                                    <option value="QualityFirst">QualityFirst</option>
                                    <option value="CostFirst">CostFirst</option>
                                    <option value="LocalFirst">LocalFirst</option>
                                </select>
                            </div>

                            <div style={styles.row}>
                                <button
                                    className="btn btn-primary"
                                    disabled={!selectedDbRouteId || loading === "db-route-test"}
                                    onClick={() =>
                                        run("db-route-test", () =>
                                            testDbAiProviderRoute(selectedDbRouteId, aiPrompt, dbFeatureKey)
                                        )
                                    }
                                >
                                    Test Selected DB Route
                                </button>

                                <button
                                    className="btn btn-primary"
                                    disabled={loading === "db-routing-test"}
                                    onClick={() =>
                                        run("db-routing-test", () =>
                                            testDbAiRouting(aiPrompt, dbFeatureKey, dbRoutingMode)
                                        )
                                    }
                                >
                                    Test DB Routing/Fallback
                                </button>
                            </div>

                            <p style={styles.hint}>
                                Use “Test Selected DB Route” to test one row directly.
                                Use “Test DB Routing/Fallback” to validate enabled/disabled priority behavior.
                            </p>
                        </div>
                    </div>

                    <p style={styles.hint}>
                        Use this to validate API keys, provider routing, latency, raw response, and image provider failures.
                    </p>
                </section>

                <section style={styles.card}>
                    <h3 style={styles.cardTitle}>Smart Redeploy Playground</h3>

                    <div style={styles.row}>
                        <label style={styles.field}>
                            <span>Module Key</span>
                            <input
                                value={smartModuleKey}
                                onChange={(e) => setSmartModuleKey(e.target.value)}
                                placeholder="products / programs / listings / coaches"
                                style={styles.input}
                            />
                        </label>

                        <button
                            className="btn btn-secondary"
                            disabled={loading === "smart-preview"}
                            onClick={() =>
                                run("smart-preview", () =>
                                    getSmartRedeployPreview(requireProjectId(), smartModuleKey)
                                )
                            }
                        >
                            Preview Affected Pages
                        </button>

                        <button
                            className="btn btn-primary"
                            disabled={loading === "smart-queue"}
                            onClick={() =>
                                run("smart-queue", () =>
                                    queueSmartRedeploy(requireProjectId(), smartModuleKey)
                                )
                            }
                        >
                            Queue Smart Redeploy
                        </button>

                        <button
                            className="btn btn-secondary"
                            disabled={loading === "redeploy-runs"}
                            onClick={() =>
                                run("redeploy-runs", () => getRedeployRuns(requireProjectId()))
                            }
                        >
                            Check Runs
                        </button>
                    </div>

                    <p style={styles.hint}>
                        Use this to debug moduleKey → affected pages → redeploy queue → generated files.
                    </p>
                </section>

                <section style={styles.card}>
                    <h3 style={styles.cardTitle}>Versioned Deploy / Rollback</h3>

                    <div style={styles.row}>
                        <button
                            className="btn btn-secondary"
                            disabled={loading === "versions"}
                            onClick={() =>
                                run("versions", () =>
                                    getDeployVersions(requireProjectId())
                                )
                            }
                        >
                            List Versions
                        </button>

                        <input
                            value={selectedVersion}
                            onChange={(e) => setSelectedVersion(e.target.value)}
                            placeholder="v20260429183010"
                            style={styles.input}
                        />

                        <button
                            className="btn btn-primary"
                            disabled={loading === "rollback" || !selectedVersion}
                            onClick={() =>
                                run("rollback", () =>
                                    rollbackVersion(requireProjectId(), selectedVersion)
                                )
                            }
                        >
                            Rollback Version
                        </button>
                    </div>

                    <p style={styles.hint}>
                        List all deployed versions. Copy a version and rollback instantly using current.json switch.
                    </p>
                </section>

                <section style={styles.card}>
                    <h3 style={styles.cardTitle}>Seeds Diagnostics</h3>

                    <div style={styles.row}>
                        <label style={styles.field}>
                            <span>Industry Filter</span>
                            <input
                                value={industry}
                                onChange={(e) => setIndustry(e.target.value)}
                                placeholder="academy / commerce / real estate"
                                style={styles.input}
                            />
                        </label>

                        <button
                            className="btn btn-secondary"
                            disabled={loading === "seeds"}
                            onClick={() => run("seeds", () => getSeedSnapshot(industry))}
                        >
                            Check Seeds
                        </button>
                    </div>
                </section>

                <section style={styles.card}>
                    <h3 style={styles.cardTitle}>Website Fix + Redeploy Playground</h3>

                    <div style={styles.row}>
                        <button
                            className="btn btn-secondary"
                            disabled={loading === "fix-identity"}
                            onClick={() =>
                                run("fix-identity", () => fixWebsiteIdentity(requireProjectId()))
                            }
                        >
                            Fix Website Identity
                        </button>

                        <button
                            className="btn btn-primary"
                            disabled={loading === "push-test-items"}
                            onClick={() =>
                                run("push-test-items", () => pushTestItems(requireProjectId(), "products"))
                            }
                        >
                            Push 2 Product Test Items + Redeploy
                        </button>

                        <button
                            className="btn btn-secondary"
                            disabled={loading === "site-snapshot"}
                            onClick={() =>
                                run("site-snapshot", () => getSiteFileSnapshot(requireProjectId()))
                            }
                        >
                            Check Generated Site File
                        </button>

                        <button
                            className="btn btn-secondary"
                            disabled={loading === "generated-files"}
                            onClick={() =>
                                run("generated-files", () => getGeneratedFiles(requireProjectId()))
                            }
                        >
                            Find Generated Files
                        </button>
                    </div>

                    <p style={styles.hint}>
                        Use this to validate CRM insert → redeploy queue → worker → generated index.html update.
                    </p>
                </section>

                <section style={styles.card}>
                    <h3 style={styles.cardTitle}>JSON Validator</h3>

                    <textarea
                        value={jsonText}
                        onChange={(e) => setJsonText(e.target.value)}
                        style={styles.textarea}
                        spellCheck={false}
                    />

                    <div style={styles.actions}>
                        <button
                            className="btn btn-secondary"
                            disabled={loading === "json"}
                            onClick={() => run("json", () => validateJson(jsonText))}
                        >
                            Validate JSON
                        </button>
                    </div>
                </section>

                {error && (
                    <section style={styles.error}>
                        <strong>Error</strong>
                        <pre>{error}</pre>
                    </section>
                )}

                <section style={styles.resultCard}>
                    <div style={styles.resultHeader}>
                        <h3 style={styles.cardTitle}>Response</h3>

                        <button
                            className="btn btn-secondary"
                            disabled={!result}
                            onClick={() =>
                                navigator.clipboard.writeText(JSON.stringify(result, null, 2))
                            }
                        >
                            Copy Response
                        </button>
                    </div>

                    {result ? (
                        <pre style={styles.pre}>{JSON.stringify(result, null, 2)}</pre>
                    ) : (
                        <div style={styles.empty}>Run a diagnostic to see response.</div>
                    )}
                </section>
            </div>
        </PageShell>
    );
}

const styles = {
    page: {
        display: "grid",
        gap: 16
    },
    hero: {
        padding: 20,
        borderRadius: 18,
        background: "#0f172a",
        color: "#fff"
    },
    title: {
        margin: 0,
        fontSize: 26
    },
    subtitle: {
        margin: "8px 0 0",
        color: "#cbd5e1"
    },
    card: {
        padding: 16,
        borderRadius: 18,
        background: "#fff",
        border: "1px solid #e5e7eb",
        boxShadow: "0 12px 30px rgba(15,23,42,.06)"
    },
    resultCard: {
        borderRadius: 18,
        background: "#fff",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        boxShadow: "0 12px 30px rgba(15,23,42,.06)"
    },
    resultHeader: {
        padding: 16,
        borderBottom: "1px solid #e5e7eb",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    },
    cardTitle: {
        margin: 0,
        fontSize: 16
    },
    row: {
        display: "flex",
        gap: 12,
        alignItems: "end",
        flexWrap: "wrap",
        marginTop: 12
    },
    field: {
        display: "grid",
        gap: 6,
        minWidth: 240,
        fontSize: 13,
        fontWeight: 700,
        color: "#475569"
    },
    input: {
        minHeight: 42,
        border: "1px solid #d1d5db",
        borderRadius: 12,
        padding: "8px 12px"
    },
    textarea: {
        width: "100%",
        minHeight: 160,
        marginTop: 12,
        border: "1px solid #d1d5db",
        borderRadius: 14,
        padding: 12,
        fontFamily: "Consolas, monospace",
        fontSize: 12
    },
    actions: {
        marginTop: 12
    },
    hint: {
        color: "#64748b",
        fontSize: 13,
        marginBottom: 0
    },
    error: {
        background: "#fee2e2",
        color: "#991b1b",
        borderRadius: 14,
        padding: 14
    },
    pre: {
        margin: 0,
        padding: 16,
        maxHeight: 520,
        overflow: "auto",
        fontSize: 12,
        background: "#020617",
        color: "#d1fae5"
    },
    empty: {
        padding: 24,
        color: "#64748b"
    }
};